import { KalturaKeyValue } from '@kaltura-ott/ott-client-types/api/types/KalturaKeyValue';
import { ssoClientInvoke } from '@kaltura-ott/tvpil';
import formatRFC3339 from 'date-fns/formatRFC3339';
import JSEncrypt from 'jsencrypt';
import jwt_decode from 'jwt-decode';
import sha256 from 'sha256';

import { LOGIN_FORM_TYPE, LOGIN_METHOD } from '../../types/stores/login-store-type';

const MAX_COUNT_REFRESH_TOKEN = 2;
export const makeKey = (loginMethod: LOGIN_METHOD, loginFormType: LOGIN_FORM_TYPE = LOGIN_FORM_TYPE.OTT) =>
  `${loginMethod}+${loginFormType}`;

export const o2RefreshToken = (
  accessToken: string,
  refreshToken: string,
  partnerId: number,
): Promise<{ accessToken: string; refreshToken: string }> => {
  return ssoClientInvoke(partnerId, {
    intent: 'Refresh Token',
    adapterData: [
      new KalturaKeyValue({
        key: 'access_token',
        value: accessToken,
      }),
      new KalturaKeyValue({
        key: 'refresh_token',
        value: refreshToken,
      }),
    ],
  }).then(({ adapterData }) => {
    return { accessToken: adapterData.access_token, refreshToken: adapterData.refresh_token };
  });
};

export const isValidAccessToken = (accessToken: string): boolean => {
  const decoded = jwt_decode<{ exp: number }>(accessToken);
  if (decoded && decoded.exp * 1000 < Date.now()) {
    return false;
  }
  return true;
};

export const getActualAccessToken = async (partnerId: number, accessToken: string, refreshToken: string) => {
  let newAccessToken = accessToken;
  let newRefreshToken = refreshToken;

  for (let i = 0; i < MAX_COUNT_REFRESH_TOKEN; i++) {
    if (isValidAccessToken(newAccessToken)) {
      return { accessToken: newAccessToken, refreshToken: newRefreshToken };
    }
    const newData = await o2RefreshToken(accessToken, refreshToken, partnerId);
    newAccessToken = newData.accessToken;
    newRefreshToken = newData.refreshToken;
  }

  return { accessToken: newAccessToken, refreshToken: newRefreshToken };
};

export function getFormatedMacAddress(mac = ''): string {
  return mac.replace(/:/g, '');
}

export interface SigningParams {
  privateKey: string;
  username: string;
  password: string;
  serviceParam: string;
  clientId: string;
  udid: string;
  signMethod?: (data: string, key: string) => Promise<string>;
}
export async function signingCertificate({
  privateKey,
  username,
  password,
  serviceParam,
  clientId,
  udid,
  signMethod,
}: SigningParams): Promise<{
  signatureTimestamp: string;
  signedString: string;
}> {
  const sign = new JSEncrypt();

  sign.setPrivateKey(privateKey);

  const currentFullDate = new Date();

  const signatureTimestamp = formatRFC3339(currentFullDate);

  const stringValue = `username=${username}&password=${password}&service=${serviceParam}&clientId=${clientId}&tvDeviceId=${udid}&signatureTimestamp=${signatureTimestamp}`;

  if (signMethod) {
    const signedString = (await signMethod(stringValue, sign.getPrivateKey())).replace(/\n/g, '');
    return { signatureTimestamp, signedString };
  }

  const signedString = sign.sign(stringValue, sha256, 'sha256') as string;

  return { signatureTimestamp, signedString };
}
